import React, { useState } from 'react';
import { X, Check, AlertCircle } from 'lucide-react';
import { useOrderContext } from '../contexts/OrderContext';
import { useToast } from '../contexts/ToastContext';
import { formatPriceWithAssetDecimals } from '../utils/priceFormatter';

const OrderConfirmationModal: React.FC = () => {
  const {
    showOrderConfirmation,
    setShowOrderConfirmation,
    orderDetails,
    executeOrder,
    setShowConfirmationPopup,
  } = useOrderContext();
  const { showToast } = useToast();

  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!showOrderConfirmation || !orderDetails) {
    return null;
  }

  const handleConfirm = async () => {
    // Clear any previous error message
    setErrorMessage(null);
    
    // If checkbox is checked, disable confirmation popup for future orders
    if (dontShowAgain) {
      setShowConfirmationPopup(false);
    }
    
    try {
      // Execute order and check if it was successful
      const result = await executeOrder();
      
      if (result.success) {
        // Show success toast if order was executed successfully
        const orderMessage = `${orderDetails.side.toUpperCase()} ${orderDetails.amount} ${orderDetails.symbol} at ${orderDetails.price}`;
        showToast(orderMessage, orderDetails.side as 'buy' | 'sell');
      } else {
        // Show error message inline in the modal
        setErrorMessage(result.error || 'Order execution failed');
      }
    } catch (error) {
      console.error('Error executing order:', error);
      setErrorMessage('An unexpected error occurred while processing your order.');
    }
  };

  const handleCancel = () => {
    setErrorMessage(null); // Clear any error message
    setShowOrderConfirmation(false);
  };

  const getOrderTypeLabel = (type: string) => {
    switch (type) {
      case 'market':
        return 'Market Order';
      case 'limit':
        return 'Limit Order';
      case 'stop':
        return 'Stop Order';
      default:
        return type;
    }
  };

  const getSideColor = (side: string) => {
    return side === 'buy' ? 'text-[#2AB7A6]' : 'text-red-500';
  };

  const getSideBgColor = (side: string) => {
    return side === 'buy' ? 'bg-[#2AB7A6]' : 'bg-red-500';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
      <div className="bg-white dark:theme-bg-secondary rounded-lg p-6 w-96 max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:theme-text-primary">
            Confirm Order
          </h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 dark:theme-text-quaternary dark:hover:theme-text-secondary"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Order Details */}
        <div className="space-y-4 mb-6">
          {/* Order Type and Side */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:theme-text-quaternary">Order Type:</span>
            <span className="text-sm font-medium text-gray-900 dark:theme-text-primary">
              {getOrderTypeLabel(orderDetails.type)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:theme-text-quaternary">Side:</span>
            <span className={`text-sm font-medium ${getSideColor(orderDetails.side)}`}>
              {orderDetails.side.toUpperCase()}
            </span>
          </div>

          {/* Symbol */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:theme-text-quaternary">Symbol:</span>
            <span className="text-sm font-medium text-gray-900 dark:theme-text-primary">
              {orderDetails.symbol}
            </span>
          </div>

          {/* Price */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:theme-text-quaternary">Price:</span>
            <span className="text-sm font-medium text-gray-900 dark:theme-text-primary">
              {formatPriceWithAssetDecimals(orderDetails.price, orderDetails.symbol)}
            </span>
          </div>

          {/* Stop Price (for stop orders) */}
          {orderDetails.type === 'stop' && orderDetails.stopPrice && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:theme-text-quaternary">Stop Price:</span>
              <span className="text-sm font-medium text-gray-900 dark:theme-text-primary">
                {formatPriceWithAssetDecimals(orderDetails.stopPrice, orderDetails.symbol)}
              </span>
            </div>
          )}

          {/* Amount */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:theme-text-quaternary">Amount:</span>
            <span className="text-sm font-medium text-gray-900 dark:theme-text-primary">
              {orderDetails.amount}
            </span>
          </div>

          {/* Leverage */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:theme-text-quaternary">Leverage:</span>
            <span className="text-sm font-medium text-gray-900 dark:theme-text-primary">
              {orderDetails.leverage}x
            </span>
          </div>

          {/* Total Position Value */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:theme-text-quaternary">Total Position Value:</span>
            <span className="text-sm font-medium text-gray-900 dark:theme-text-primary">
              ${(orderDetails.price * orderDetails.amount).toFixed(2)}
            </span>
          </div>

          {/* Margin Required */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:theme-border">
            <span className="text-sm font-medium text-gray-900 dark:theme-text-primary">Margin Required:</span>
            <span className="text-sm font-bold text-gray-900 dark:theme-text-primary">
              ${((orderDetails.price * orderDetails.amount) / orderDetails.leverage).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Warning */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-6">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
            <div className="text-sm text-yellow-800 dark:text-yellow-200">
              <p className="font-medium">Please review your order details carefully.</p>
              <p className="mt-1">This action cannot be undone once confirmed.</p>
            </div>
          </div>
        </div>

        {/* Don't Show Again Checkbox */}
        <div className="mb-6">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 text-[#2AB7A6] bg-gray-100 border-gray-300 rounded focus:ring-[#2AB7A6] focus:ring-2"
            />
            <span className="text-sm text-gray-600 dark:theme-text-quaternary">
              Do not show this confirmation popup again
            </span>
          </label>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-500 mr-2 flex-shrink-0" />
              <span className="text-sm text-red-800 dark:text-red-200 font-medium">
                {errorMessage}
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:theme-text-secondary bg-gray-100 dark:theme-bg-quaternary rounded-lg hover:bg-gray-200 dark:hover:theme-bg-quinary transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors duration-200 ${getSideBgColor(orderDetails.side)} hover:opacity-90`}
          >
            <Check className="h-4 w-4 inline mr-2" />
            Confirm {orderDetails.side.toUpperCase()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationModal;
